import React from "react";
import { Box } from "@mui/material";

const Preloader = ({ 
  loading = true,
  primaryColor = "#2E7D32",
  secondaryColor = "#4CAF50",
  accentColor = "#66BB6A",
  size = 80,
  fullScreen = true,
}) => {
  if (!loading) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        ...(fullScreen && {
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 9999,
        }),
        background: `
          radial-gradient(
            ellipse at 20% 50%,
            rgba(255, 255, 255, 1) 0%,
            rgba(245, 248, 240, 1) 50%,
            rgba(232, 245, 233, 1) 100%
          )
        `,
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-50%",
          right: "-50%",
          width: "200%",
          height: "200%",
          background: `
            radial-gradient(
              circle at 70% 30%,
              ${primaryColor}08 0%,
              transparent 50%
            ),
            radial-gradient(
              circle at 30% 70%,
              ${secondaryColor}08 0%,
              transparent 50%
            )
          `,
          animation: "backgroundPulse 15s ease-in-out infinite",
          "@keyframes backgroundPulse": {
            "0%, 100%": {
              transform: "scale(1) rotate(0deg)",
            },
            "33%": {
              transform: "scale(1.1) rotate(120deg)",
            },
            "66%": {
              transform: "scale(0.9) rotate(240deg)",
            },
          },
        },
      }}
    >
      {/* Main Loader Container */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 4,
        }}
      >
        {/* The Orb */}
        <Box
          sx={{
            position: "relative",
            width: size * 1.8,
            height: size * 1.8,
          }}
        >
          {/* Glow Rings */}
          {[...Array(4)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: size * (1.2 + i * 0.3),
                height: size * (1.2 + i * 0.3),
                borderRadius: "50%",
                border: `1.5px solid ${primaryColor}${15 + i * 8}`,
                animation: `ringExpand ${3 + i * 0.5}s ease-out infinite`,
                animationDelay: `${i * 0.3}s`,
                "@keyframes ringExpand": {
                  "0%": {
                    transform: "translate(-50%, -50%) scale(0.5)",
                    opacity: 0.8,
                  },
                  "100%": {
                    transform: "translate(-50%, -50%) scale(1.5)",
                    opacity: 0,
                  },
                },
              }}
            />
          ))}

          {/* Main Orb */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: size,
              height: size,
              borderRadius: "50%",
              background: `
                radial-gradient(
                  circle at 35% 35%,
                  ${secondaryColor} 0%,
                  ${primaryColor} 60%,
                  ${primaryColor}DD 100%
                )
              `,
              boxShadow: `
                0 0 60px ${primaryColor}40,
                0 0 120px ${primaryColor}20,
                0 0 200px ${primaryColor}10,
                inset 0 -20px 60px rgba(0,0,0,0.2),
                inset 0 20px 60px rgba(255,255,255,0.1)
              `,
              animation: "orbFloat 4s ease-in-out infinite",
              "&::before": {
                content: '""',
                position: "absolute",
                top: "15%",
                left: "20%",
                width: "30%",
                height: "20%",
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 100%)",
                transform: "rotate(-30deg)",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: "20%",
                right: "25%",
                width: "15%",
                height: "15%",
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 100%)",
                transform: "rotate(30deg)",
              },
              "@keyframes orbFloat": {
                "0%, 100%": {
                  transform: "translate(-50%, -50%) scale(1)",
                },
                "50%": {
                  transform: "translate(-50%, -60%) scale(1.02)",
                },
              },
            }}
          >
            {/* Orbital Rings */}
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "140%",
                height: "140%",
                borderRadius: "50%",
                border: `2px solid ${primaryColor}20`,
                animation: "orbitRing 6s linear infinite",
                "@keyframes orbitRing": {
                  "0%": {
                    transform: "translate(-50%, -50%) rotate(0deg)",
                  },
                  "100%": {
                    transform: "translate(-50%, -50%) rotate(360deg)",
                  },
                },
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "170%",
                height: "170%",
                borderRadius: "50%",
                border: `1.5px solid ${secondaryColor}15`,
                animation: "orbitRing 8s linear infinite reverse",
                "@keyframes orbitRing": {
                  "0%": {
                    transform: "translate(-50%, -50%) rotate(0deg)",
                  },
                  "100%": {
                    transform: "translate(-50%, -50%) rotate(360deg)",
                  },
                },
              }}
            />
          </Box>

          {/* Orbiting Particles */}
          {[...Array(12)].map((_, i) => {
            const angle = (i / 12) * 360;
            const distance = size * 1.2;
            return (
              <Box
                key={i}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  width: 4 + (i % 3) * 2,
                  height: 4 + (i % 3) * 2,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${primaryColor}, ${secondaryColor})`,
                  transform: `rotate(${angle}deg) translateX(${distance}px) rotate(-${angle}deg)`,
                  animation: `particleOrbit ${4 + (i % 3)}s ease-in-out infinite`,
                  animationDelay: `${i * 0.1}s`,
                  opacity: 0.3 + (i / 12) * 0.7,
                  boxShadow: `0 0 20px ${primaryColor}30`,
                  "@keyframes particleOrbit": {
                    "0%, 100%": {
                      transform: `rotate(${angle}deg) translateX(${distance}px) rotate(-${angle}deg) scale(0.5)`,
                      opacity: 0.2,
                    },
                    "50%": {
                      transform: `rotate(${angle + 180}deg) translateX(${distance * 1.1}px) rotate(-${angle - 180}deg) scale(1.5)`,
                      opacity: 1,
                    },
                  },
                }}
              />
            );
          })}
        </Box>

        {/* Brand Name and Tagline */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {/* Brand Name */}
          <Box
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              letterSpacing: 3,
              color: primaryColor,
              textTransform: "uppercase",
              animation: "fadeInOut 2s ease-in-out infinite",
              "@keyframes fadeInOut": {
                "0%, 100%": {
                  opacity: 0.6,
                },
                "50%": {
                  opacity: 1,
                },
              },
            }}
          >
           Raw Materials Research and Development Council (RMRDC) 
          </Box>

          {/* Tagline with Typewriter Effect */}
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Box
              sx={{
                fontSize: "11px",
                fontWeight: 300,
                letterSpacing: 4,
                color: "rgba(0,0,0,0.4)",
                textTransform: "uppercase",
                animation: "fadeInOut 2s ease-in-out infinite",
                "@keyframes fadeInOut": {
                  "0%, 100%": {
                    opacity: 0.3,
                  },
                  "50%": {
                    opacity: 0.8,
                  },
                },
              }}
            >
              Chemical Market
            </Box>
            <Box
              sx={{
                width: 4,
                height: 14,
                background: `linear-gradient(180deg, ${primaryColor}, ${secondaryColor})`,
                animation: "blink 1s step-end infinite",
                "@keyframes blink": {
                  "0%, 100%": {
                    opacity: 1,
                  },
                  "50%": {
                    opacity: 0,
                  },
                },
              }}
            />
          </Box>

          {/* Progress Dots */}
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              mt: 0.5,
            }}
          >
            {[...Array(5)].map((_, i) => (
              <Box
                key={i}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                  animation: `dotProgress 1.5s ease-in-out infinite`,
                  animationDelay: `${i * 0.15}s`,
                  opacity: 0.2,
                  "@keyframes dotProgress": {
                    "0%, 100%": {
                      transform: "scale(0.5)",
                      opacity: 0.2,
                    },
                    "50%": {
                      transform: "scale(1.5)",
                      opacity: 1,
                    },
                  },
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Decorative Corner Elements */}
        <Box
          sx={{
            position: "absolute",
            top: -60,
            right: -60,
            width: 120,
            height: 120,
            border: `1px solid ${primaryColor}08`,
            transform: "rotate(45deg)",
            animation: "cornerPulse 4s ease-in-out infinite",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -60,
            left: -60,
            width: 120,
            height: 120,
            border: `1px solid ${primaryColor}08`,
            transform: "rotate(45deg)",
            animation: "cornerPulse 4s ease-in-out infinite 0.5s",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: -60,
            left: -60,
            width: 60,
            height: 60,
            border: `1px solid ${secondaryColor}10`,
            transform: "rotate(45deg)",
            animation: "cornerPulse 4s ease-in-out infinite 1s",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -60,
            right: -60,
            width: 60,
            height: 60,
            border: `1px solid ${secondaryColor}10`,
            transform: "rotate(45deg)",
            animation: "cornerPulse 4s ease-in-out infinite 1.5s",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: -80,
            width: 80,
            height: 80,
            border: `1px solid ${primaryColor}05`,
            transform: "rotate(45deg)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            right: -80,
            width: 80,
            height: 80,
            border: `1px solid ${primaryColor}05`,
            transform: "rotate(45deg)",
          }}
        />
        <Box
          sx={{
            "@keyframes cornerPulse": {
              "0%, 100%": {
                opacity: 0.3,
                transform: "rotate(45deg) scale(1)",
              },
              "50%": {
                opacity: 1,
                transform: "rotate(45deg) scale(1.1)",
              },
            },
          }}
        />
      </Box>

      {/* Background Particles */}
      {[...Array(30)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            width: 1 + Math.random() * 3,
            height: 1 + Math.random() * 3,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${primaryColor}${20 + Math.random() * 30}, transparent)`,
            animation: `floatParticle ${10 + Math.random() * 15}s linear infinite`,
            animationDelay: `${Math.random() * 15}s`,
            left: `${Math.random() * 100}%`,
            bottom: "-20px",
            opacity: 0,
            "@keyframes floatParticle": {
              "0%": {
                transform: "translateY(0) translateX(0) scale(0)",
                opacity: 0,
              },
              "10%": {
                opacity: 0.3 + Math.random() * 0.5,
              },
              "90%": {
                opacity: 0.3 + Math.random() * 0.5,
              },
              "100%": {
                transform: `translateY(-100vh) translateX(${Math.random() * 200 - 100}px) scale(1)`,
                opacity: 0,
              },
            },
          }}
        />
      ))}
    </Box>
  );
};

export default Preloader;